import { useEffect, useState, useRef, cloneElement } from 'react'
import PropTypes from 'prop-types'
import { useStickyContext } from './Container'

const Sticky = props => {
  const {
    children,
    relative,
    bottomOffset,
    topOffset,
    disableHardwareAcceleration,
    disableCompensation
  } = props
  const [stickyState, setStickyState] = useState({
    isSticky: false,
    wasSticky: false,
    style: {}
  })
  const { subscribe, unsubscribe, getParent } = useStickyContext()
  const placeholderRef = useRef()
  const contentRef = useRef()

  const handleContainerEvent = ({
    distanceFromTop,
    distanceFromBottom,
    eventSource
  }) => {
    const parent = getParent()
    const placeholder = placeholderRef.current
    let preventingStickyStateChanges = false

    if (relative) {
      preventingStickyStateChanges = eventSource !== parent
      distanceFromTop =
        -(eventSource.scrollTop + eventSource.offsetTop) + placeholder.offsetTop
    }

    const placeholderClientRect = placeholder.getBoundingClientRect()
    const contentClientRect = contentRef.current.getBoundingClientRect()
    const calculatedHeight = contentClientRect.height
    const bottomDifference =
      distanceFromBottom - bottomOffset - calculatedHeight
    const wasSticky = !!stickyState.isSticky
    const isSticky = preventingStickyStateChanges
      ? wasSticky
      : distanceFromTop <= -topOffset && distanceFromBottom > -bottomOffset
    const style = !isSticky
      ? {}
      : {
        position: 'fixed',
        top:
          bottomDifference > 0
            ? relative
              ? parent.offsetTop - parent.offsetParent.scrollTop
              : 0
            : bottomDifference,
        left: placeholderClientRect.left,
        width: placeholderClientRect.width
      }

    if (!disableHardwareAcceleration) {
      style.transform = 'translateZ(0)'
    }

    setStickyState({
      isSticky,
      wasSticky,
      distanceFromTop,
      distanceFromBottom:
        (relative
          ? parent.scrollHeight - parent.scrollTop
          : distanceFromBottom) - calculatedHeight,
      calculatedHeight,
      style
    })
  }

  useEffect(() => {
    if (!subscribe) {
      throw new TypeError(
        'Expected Sticky to be mounted within StickyContainer'
      )
    }
    subscribe(handleContainerEvent)
    return unsubscribe
  }, [])

  useEffect(() => {
    const placeholder = placeholderRef.current
    placeholder.style.paddingBottom = disableCompensation
      ? 0
      : `${stickyState.isSticky ? stickyState.calculatedHeight : 0}px`
  })

  const element = cloneElement(children(stickyState), {
    ref: contentRef,
    key: 'sticky-element'
  })

  return (
    <div>
      <div
        ref={placeholderRef}
        key='placeholder'
      />
      {element}
    </div>
  )
}

Sticky.propTypes = {
  topOffset: PropTypes.number,
  bottomOffset: PropTypes.number,
  relative: PropTypes.bool,
  children: PropTypes.func.isRequired
}

Sticky.defaultProps = {
  relative: false,
  topOffset: 0,
  bottomOffset: 0,
  disableCompensation: false,
  disableHardwareAcceleration: false
}

export default Sticky
